import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import React from "react";
import {
  API_BASE,
  fetchSearch,
  SEARCH_PAGE_SIZE,
  PAGE_SIZE,
  fetchFormats,
  fetchJson,
} from "../utils";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import FilterSection from "../components/FilterSection";
import CatalogSearch from "../components/CatalogSearch";
import SortDropdown from "../components/SortDropdown";
import useCatalogFilters from "../hooks/useCatalogFilters";
import useCatalogResults from "../hooks/useCatalogResults";
import TrashIcon from "../components/TrashIcon";
const FILTER_DEFAULTS = {
  artistQuery: "",
  artistFormat: [],
  artistSort: "name",
};
function ArtistList({
  onSelect,
  view = "artist",
  onViewChange,
  refreshRevision = 0,
}) {
  const [filters, setFilter] = useCatalogFilters(FILTER_DEFAULTS);
  const query = filters.artistQuery;
  const setQuery = (value) => setFilter("artistQuery", value);
  const selectedFormats = filters.artistFormat;
  const setSelectedFormats = (value) => setFilter("artistFormat", value);
  const sort = filters.artistSort;
  const setSort = (value) => setFilter("artistSort", value);
  const [formatOptions, setFormatOptions] = React.useState([]);
  const [optionError, setOptionError] = React.useState(null);
  const [optionRevision, setOptionRevision] = React.useState(0);
  const loadMoreRef = React.useRef(null);
  const loadPage = React.useCallback(
    async (page, term, signal) => {
      const requestFilters = {
        format: selectedFormats,
        sort: sort,
      };
      if (term) {
        const data = await fetchSearch({
          query: term,
          page,
          types: "artist",
          filters: requestFilters,
          signal,
        });
        return {
          items: data.results || [],
          pageSize: SEARCH_PAGE_SIZE,
        };
      }
      const searchParams = new URLSearchParams({
        page,
        page_size: PAGE_SIZE,
      });
      Object.entries(requestFilters).forEach(([key, value]) => {
        if (Array.isArray(value))
          value.forEach((item) => searchParams.append(key, item));
        else if (value) searchParams.set(key, value);
      });
      const data = await fetchJson(
        `${API_BASE}/artists/?${searchParams.toString()}`,
        {
          credentials: "include",
          signal,
        },
      );
      return {
        items: data.artists || data.results || [],
        pageSize: PAGE_SIZE,
      };
    },
    [selectedFormats, sort],
  );
  const {
    items: artists,
    hasMore,
    loading,
    fetched,
    error,
    loadMore,
    retry,
  } = useCatalogResults({
    query,
    loadPage,
    refreshRevision,
    enabled: true,
  });
  React.useEffect(() => {
    let active = true;
    setOptionError(null);
    fetchFormats()
      .then((options) => {
        if (active) setFormatOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    return () => {
      active = false;
    };
  }, [optionRevision]);
  React.useEffect(() => {
    if (!loadMoreRef.current || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      {
        rootMargin: "0px 0px 600px 0px",
      },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loadMore, artists.length]);
  return (
    <div>
      {optionError && (
        <Alert role="alert" severity="error">
          Some filters could not be loaded: {optionError.message}
          <Button
            className="btn"
            onClick={() => setOptionRevision((value) => value + 1)}
          >
            Retry filters
          </Button>
        </Alert>
      )}
      {error && (
        <Alert role="alert" severity="error">
          Could not load artists: {error.message}{" "}
          <Button type="button" className="btn" onClick={retry}>
            Retry
          </Button>
        </Alert>
      )}
      <div className="search-bar">
        <CatalogSearch
          view={view}
          onViewChange={onViewChange}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
        />
      </div>
      <FilterSection
        sortControl={
          <SortDropdown
            value={sort}
            onChange={setSort}
            options={[
              {
                value: "name",
                label: "Name A-Z",
              },
              {
                value: "-name",
                label: "Name Z-A",
              },
            ]}
          />
        }
      >
        <div className="filter-row">
          <MultiSelectDropdown
            label="Format"
            options={formatOptions}
            selected={selectedFormats}
            onChange={setSelectedFormats}
            className="filter-dropdown"
          />
        </div>
      </FilterSection>
      {selectedFormats.length > 0 && (
        <div className="applied-filters">
          {selectedFormats.map((f) => (
            <Button
              key={f}
              type="button"
              className="filter-tag"
              aria-label={`Remove format ${f}`}
              onClick={() =>
                setSelectedFormats((prev) => prev.filter((x) => x !== f))
              }
            >
              {f}
              <CloseIcon />
            </Button>
          ))}
          <IconButton
            type="button"
            className="filter-tag clear-filters"
            aria-label="Clear all filters"
            onClick={() => {
              setSelectedFormats([]);
              setSort("name");
            }}
          >
            <TrashIcon />
          </IconButton>
        </div>
      )}
      <ul className="album-grid">
        {artists.map((artist) => (
          <li
            key={artist.id}
            className="album-tile compact-catalog-card collection-artist-card"
            onClick={() => onSelect && onSelect(artist.id)}
          >
            <div className="album-thumb">
              <img
                src={`${API_BASE}/artists/${artist.id}/image/`}
                alt={`${artist.name}`}
                loading="lazy"
              />
            </div>
            <div className="album-info">
              <div className="album-title">
                <Button
                  type="button"
                  className="catalog-title-button"
                  variant="text"
                  disableRipple
                  title={artist.name}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect && onSelect(artist.id);
                  }}
                >
                  <span>{artist.name}</span>
                </Button>
              </div>
            </div>
          </li>
        ))}
        {loading && (
          <li
            className="album-tile"
            role="status"
            style={{
              textAlign: "center",
            }}
          >
            Loading...
          </li>
        )}
        {!loading && !error && fetched && artists.length === 0 && (
          <li
            className="album-tile"
            style={{
              textAlign: "center",
            }}
          >
            No results
          </li>
        )}
        {artists.length > 0 && hasMore && (
          <li
            ref={loadMoreRef}
            style={{
              listStyle: "none",
            }}
          />
        )}
      </ul>
      {hasMore && (
        <Button
          className="btn"
          disabled={loading || !!error}
          onClick={loadMore}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
export default ArtistList;
